/**
 * Client Service - CRUD Operations and Sync
 *
 * Data reading should be done via useLiveQuery from Dexie React hooks.
 * This service provides CRUD operations, utility functions, and server sync.
 */

import db from '@/db';
import { Client, decorateClient } from '../types/client';
import { API_ENDPOINTS } from '../../config/env';
import { getCurrentPerson } from './user_service';
import { fetchCollection } from '../../lib/paged-collection';
import { forgetCollection } from './http_cache_service';
import { documentNeedsSync } from './document_service';

import { createLogger, LogLevel } from '../../lib/logger';

const logger = createLogger('ClientService', LogLevel.DEBUG);
/**
 * Sorts clients by updated_at (most recent first), then by name
 * @param clients Array of clients to sort
 * @returns New sorted array of clients
 */
export function sortClientsByRecentlyViewed(clients: Client[]): Client[] {
  return [...clients].sort((a, b) => {
    // Primary sort: updated_at (most recent first)
    if (a.updated_at && b.updated_at) {
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    }

    // If one has updated_at and the other doesn't, prioritize the one with updated_at
    if (a.updated_at && !b.updated_at) return -1;
    if (!a.updated_at && b.updated_at) return 1;

    // Secondary sort: by name alphabetically
    return decorateClient(a).fullName.localeCompare(decorateClient(b).fullName);
  });
}

// API may return { records: [...] }, { clients: [...] }, or just [...]
function parseClientsPage(data: unknown): Client[] | null {
  const body = data as Client[] | { records?: Client[]; clients?: Client[] } | null;
  const clients = Array.isArray(body) ? body : body?.records || body?.clients;

  if (!clients || !Array.isArray(clients)) {
    logger.warn('[ClientService] Unexpected API response format:', data);
    return null;
  }

  return clients;
}

/**
 * Persist an array of clients (and their contacts) into IndexedDB.
 */
async function storeClients(clients: Client[], organizationId: number): Promise<void> {
  await db.transaction('rw', [db.clients, db.contacts], async () => {
    // Preserve local-only fields (last_viewed_at, eTag, lastModified) that
    // are not returned by the API and would otherwise be wiped by bulkPut.
    const existingClients = await db.clients.where('id').anyOf(clients.map(c => c.id)).toArray();
    const existingMap = new Map(existingClients.map(c => [c.id, c]));

    const clientsWithMeta = clients.map(client => {
      const existing = existingMap.get(client.id);
      return {
        ...client,
        organization_id: organizationId,
        syncedOffline: true,
        last_viewed_at: existing?.last_viewed_at,
        eTag: existing?.eTag,
        lastModified: existing?.lastModified,
      };
    });

    await db.clients.bulkPut(clientsWithMeta);

    // Extract and store all contacts from clients, preserving local-only fields
    const flatContacts = clients.flatMap(client =>
      (client.contacts || []).map(contact => ({ contact, clientId: client.id }))
    );
    const contactIds = flatContacts.map(c => c.contact.id).filter((id): id is number => id != null);
    const existingContacts = await db.contacts.where('id').anyOf(contactIds).toArray();
    const existingContactMap = new Map(existingContacts.map(c => [c.id, c]));

    const allContacts = flatContacts.map(({ contact, clientId }) => {
      const existing = existingContactMap.get(contact.id);
      return {
        ...contact,
        customer_id: clientId,
        organization_id: organizationId,
        local_id: existing?.local_id,
        synced: existing?.synced === false ? false : true,
      };
    });

    if (allContacts.length > 0) {
      await db.contacts.bulkPut(allContacts);
      logger.debug(`[ClientService] Stored ${allContacts.length} contacts`);
    }
  });
}

/**
 * Refresh clients from the server.
 * Every page is fetched with its own conditional GET (lib/paged-collection):
 * rows from changed pages are stored, and clients the server no longer lists
 * are pruned once every page has answered.
 */
export async function refreshClients(): Promise<void> {
  const currentPerson = await getCurrentPerson();

  if (!currentPerson) {
    logger.debug('[ClientService] No current person - skipping refresh');
    return;
  }

  const organizationId = currentPerson.organization.id;

  const collection = await fetchCollection<Client>({
    organizationId,
    cacheKey: `clients_person_${currentPerson.id}`,
    url: page => `${API_ENDPOINTS.clients}?current_person_id=${currentPerson.id}&page=${page}`,
    parse: parseClientsPage,
    // Scoped like the prune below: a row filed under another organization does not vouch for this one's page.
    localIds: async ids => new Set(await db.clients.where('id').anyOf(ids as number[]).filter(c => c.organization_id === organizationId).primaryKeys()),
    timeout: 30000,
  });

  if (!collection.firstPageOk) {
    if (collection.status === 404) {
      logger.debug('[ClientService] Clients endpoint not available');
      return;
    }
    if (collection.httpOk) return; // Unusable body — already logged by the parser.
    if (collection.status === 304) return; // A 304 with no manifest to read it against; keep local rows, ask again next time.
    throw new Error(`Failed to fetch clients: ${collection.status}`);
  }

  if (collection.records.length > 0) await storeClients(collection.records, organizationId);
  await collection.commit();

  if (!collection.complete) {
    logger.warn('[ClientService] The clients walk was incomplete; keeping local clients until the next refresh');
    return;
  }

  // Reconcile on every complete walk, unchanged or not: a client that fell off
  // the server list is removed (or protected while it has pending documents),
  // and one kept only for pending documents is released once those have synced.
  const fetchedIds = collection.ids;
  const localClients = await db.clients.where('organization_id').equals(organizationId).toArray();
  const staleIds: number[] = [];

  for (const client of localClients) {
    if (fetchedIds.has(client.id)) {
      // Client is in server response — clear protected flag if it was set
      if (client._locally_protected) {
        await db.clients.update(client.id, { _locally_protected: undefined });
      }
      continue;
    }

    const docs = await db.documents.where('client_id').equals(client.id).toArray();
    const hasPendingDocs = docs.some(d =>
      documentNeedsSync(d)
    );
    // A note written for a client who left the caseload stays (with the
    // reason Mon Ami gives) until the user copies and discards it.
    const hasPendingNotes = await db.caseNotes.where('client_id').equals(client.id).filter(n => !n.synced).count() > 0;

    if (hasPendingDocs || hasPendingNotes) {
      // Mark as protected so the next complete walk re-evaluates it
      await db.clients.update(client.id, { _locally_protected: true });
    } else {
      staleIds.push(client.id);
    }
  }

  if (staleIds.length > 0) {
    await db.transaction('rw', [db.clients, db.contacts, db.caseNotes], async () => {
      await db.clients.bulkDelete(staleIds);
      await db.contacts.where('customer_id').anyOf(staleIds).delete();
      await db.caseNotes.where('client_id').anyOf(staleIds).delete();
    });
    for (const id of staleIds) await forgetClientDocuments(organizationId, id);
    logger.debug(`[ClientService] Removed ${staleIds.length} stale client(s) no longer in API`);
  }

  logger.debug(collection.unchanged
    ? `[ClientService] Clients unchanged across ${collection.totalPages} page(s)`
    : `[ClientService] Refreshed ${collection.records.length} changed client(s) across ${collection.totalPages} page(s)`);
}

// A removed client's documents and notes are never refreshed again; drop the
// per-page validators and manifests their refreshes kept for them.
async function forgetClientDocuments(organizationId: number, clientId: number): Promise<void> {
  await forgetCollection(organizationId, `documents_${clientId}`);
  await forgetCollection(organizationId, `notes_${clientId}`);
}
